import { useEffect, useRef, useState } from 'react';

/**
 * Entwürfe mit Versionsnummer speichern (F01 aus dem Prüfbericht 0.41):
 * höchstens ein Speichervorgang gleichzeitig. Änderungen während eines laufenden Vorgangs bekommen
 * eine lokale Änderungsnummer und werden danach mit der bestätigten Revision nachgespeichert.
 * „Gespeichert“ gilt erst, wenn keine neuere Änderung mehr offen ist.
 */
export type SaveFailure = 'conflict' | 'gone' | 'offline' | 'error';
export type SerialState<R> = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; result: R } | { kind: 'failed'; reason: SaveFailure; message?: string };

export interface SerialSaverOptions<T, R extends { revision: number }> {
  send: (value: T, revision: number) => Promise<R>;
  /** Fehler einordnen; bei 'offline' wird später erneut versucht, bei 'conflict'/'gone' erst nach Entscheidung */
  classify: (e: unknown) => SaveFailure;
  onState: (s: SerialState<R>) => void;
  delayMs?: number;
}

export class SerialSaver<T, R extends { revision: number }> {
  revision = 0;
  private value: T | undefined;
  private seq = 0;
  private savedSeq = 0;
  private running: Promise<void> | null = null;
  private blocked: SaveFailure | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private closed = false;

  constructor(private o: SerialSaverOptions<T, R>) {}

  setOptions(o: SerialSaverOptions<T, R>) {
    this.o = o;
  }

  /** Stand vom Server übernehmen (Laden, Neu laden) – nichts offen */
  reset(value: T, revision: number) {
    clearTimeout(this.timer);
    this.value = value;
    this.revision = revision;
    this.savedSeq = this.seq;
    this.blocked = null;
    this.o.onState({ kind: 'idle' });
  }

  /** Lokale Änderung: merken und verzögert speichern */
  change(value: T) {
    this.value = value;
    this.seq++;
    clearTimeout(this.timer);
    if (!this.blocked && !this.closed) this.timer = setTimeout(() => void this.flush(), this.o.delayMs ?? 800);
  }

  get dirty() {
    return this.seq > this.savedSeq;
  }
  get blockedBy() {
    return this.blocked;
  }

  /** Sofort speichern; wartet auch auf einen laufenden Vorgang samt Nachspeichern */
  flush(): Promise<void> {
    clearTimeout(this.timer);
    if (this.running) return this.running;
    if (!this.dirty || this.blocked || this.closed) return Promise.resolve();
    this.running = this.loop().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async loop() {
    while (this.dirty && !this.blocked && !this.closed) {
      const seq = this.seq;
      this.o.onState({ kind: 'saving' });
      try {
        const r = await this.o.send(this.value as T, this.revision);
        this.revision = r.revision;
        this.savedSeq = Math.max(this.savedSeq, seq);
        if (!this.dirty) this.o.onState({ kind: 'saved', result: r });
      } catch (e) {
        const reason = this.o.classify(e);
        if (reason !== 'offline') this.blocked = reason;
        this.o.onState({ kind: 'failed', reason, message: (e as Error)?.message });
        return;
      }
    }
  }

  /** Echter Konflikt: eigene Eingaben bewusst über den neueren Serverstand schreiben */
  overwrite(serverRevision: number): Promise<void> {
    this.revision = serverRevision;
    this.blocked = null;
    if (!this.dirty) this.seq++;
    return this.flush();
  }

  /** Nach dem Absenden oder Verwerfen: nichts mehr speichern */
  close() {
    clearTimeout(this.timer);
    this.closed = true;
  }
  open() {
    this.closed = false;
  }
}

/** React-Hülle: Zustand für die Anzeige, Wiederholung bei Verbindung, Warnung beim Verlassen */
export function useSerialSave<T, R extends { revision: number }>(opts: Omit<SerialSaverOptions<T, R>, 'onState'>) {
  const [state, setState] = useState<SerialState<R>>({ kind: 'idle' });
  const ref = useRef<SerialSaver<T, R> | null>(null);
  if (!ref.current) ref.current = new SerialSaver<T, R>({ ...opts, onState: setState });
  ref.current.setOptions({ ...opts, onState: setState });
  const saver = ref.current;
  useEffect(() => {
    const retry = () => {
      if (saver.dirty && !saver.blockedBy) void saver.flush();
    };
    window.addEventListener('online', retry);
    const t = window.setInterval(retry, 15_000);
    const leave = (e: BeforeUnloadEvent) => {
      if (!saver.dirty) return;
      void saver.flush();
      e.preventDefault();
    };
    window.addEventListener('beforeunload', leave);
    return () => {
      window.removeEventListener('online', retry);
      window.removeEventListener('beforeunload', leave);
      window.clearInterval(t);
      // Beim Verlassen der Seite innerhalb der App: Offenes noch absenden
      void saver.flush();
    };
  }, [saver]);
  return { saver, state, setState };
}
